import { createTheme } from '@mui/material/styles';

export const brand = {
  ink: '#0B2038',
  inkSoft: '#143452',
  paper: '#F8F5EF',
  paperDeep: '#F1EDE5',
  mist: '#E8EFF3',
  sky: '#86AAC4',
  sage: '#7E9B86',
  mint: '#B9D8CA',
  butter: '#86AAC4',
  butterHover: '#759DB8',
  steel: '#5F6F7B',
  white: '#FFFFFF',
  border: 'rgba(11, 32, 56, 0.12)',
  shadowFlat: '0 1px 2px rgba(11, 32, 56, 0.04)',
  shadowRaised: '0 10px 28px rgba(11, 32, 56, 0.10)',
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brand.ink,
      contrastText: brand.white,
    },
    secondary: {
      main: brand.sky,
      contrastText: brand.ink,
    },
    warning: {
      main: '#D6A93B',
      contrastText: brand.ink,
    },
    background: {
      default: brand.paper,
      paper: brand.white,
    },
    text: {
      primary: brand.ink,
      secondary: brand.steel,
    },
    divider: brand.border,
    success: {
      main: brand.sage,
      contrastText: brand.white,
    },
    error: {
      main: '#C94B40',
      contrastText: brand.white,
    },
    info: {
      main: brand.sky,
      contrastText: brand.ink,
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h1: { fontFamily: "'Bricolage Grotesque', 'Inter', sans-serif", fontWeight: 700, letterSpacing: 0 },
    h2: { fontFamily: "'Bricolage Grotesque', 'Inter', sans-serif", fontWeight: 700, letterSpacing: 0 },
    h3: { fontFamily: "'Bricolage Grotesque', 'Inter', sans-serif", fontWeight: 700, letterSpacing: 0 },
    h4: { fontWeight: 700, letterSpacing: 0 },
    h5: { fontWeight: 700, letterSpacing: 0 },
    h6: { fontWeight: 700, letterSpacing: 0 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&:focus, &:focus-visible, &:active': {
            outline: 'none',
            boxShadow: 'none',
          },
          '&.Mui-focusVisible': {
            outline: 'none',
            boxShadow: 'none',
          }
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 36,
          transition: `background-color 160ms ${brand.ease}, border-color 160ms ${brand.ease}, box-shadow 160ms ${brand.ease}, transform 160ms ${brand.ease}`,
          '&:focus, &:focus-visible, &:active, &.Mui-focusVisible': {
            outline: 'none',
            boxShadow: 'none',
          },
          '&:active': {
            transform: 'scale(0.985)',
          },
        },
        containedPrimary: {
          backgroundColor: brand.butter,
          color: brand.ink,
          boxShadow: brand.shadowFlat,
          border: `1px solid ${brand.border}`,
          '&:hover': {
            backgroundColor: brand.butterHover,
            boxShadow: brand.shadowRaised,
          }
        },
        outlined: {
          borderColor: brand.border,
          color: brand.ink,
        },
        text: {
          color: brand.ink,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${brand.border}`,
          borderRadius: 8,
          boxShadow: brand.shadowFlat,
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brand.white,
          color: brand.ink,
          backgroundImage: 'none',
          boxShadow: 'none',
        }
      }
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: brand.butter,
          color: brand.ink,
          boxShadow: brand.shadowRaised,
          '&:hover': {
            backgroundColor: brand.butterHover,
            boxShadow: brand.shadowRaised,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: brand.shadowFlat,
        },
        elevation8: {
          boxShadow: brand.shadowRaised,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: brand.mist,
          color: brand.ink,
          fontWeight: 700,
        },
      },
    },
  },
});

export default theme;
